import apiClient from './api';

class FaqService {
  /**
   * Get all FAQs with optional filters
   * @param {Object} params - Query parameters {status, is_active, search, ordering, page}
   * @returns {Promise} - List of FAQs
   */
  async getFaqs(params = {}) {
    try {
      const response = await apiClient.get('/admin/faqs/', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create FAQ as draft
   * @param {Object} data - {question, answer, order, is_active}
   * @returns {Promise} - Created FAQ
   */
  async createFaq(data) {
    try {
      const response = await apiClient.post('/admin/faqs/', data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating FAQ:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create and publish FAQ immediately
   * @param {Object} data - {question, answer, order, is_active}
   * @returns {Promise} - Created and published FAQ
   */
  async createAndPublishFaq(data) {
    try {
      const response = await apiClient.post('/admin/faqs/create_and_publish/', data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating and publishing FAQ:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get single FAQ by ID
   * @param {number|string} id - FAQ ID
   * @returns {Promise} - FAQ details
   */
  async getFaqById(id) {
    try {
      const response = await apiClient.get(`/admin/faqs/${id}/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching FAQ:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update FAQ (full update)
   * @param {number|string} id - FAQ ID
   * @param {Object} data - Complete FAQ data
   * @returns {Promise} - Updated FAQ
   */
  async updateFaq(id, data) {
    try {
      const response = await apiClient.put(`/admin/faqs/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating FAQ:', error);
      return this.handleError(error);
    }
  }

  /**
   * Partial update FAQ
   * @param {number|string} id - FAQ ID
   * @param {Object} data - Partial FAQ data
   * @returns {Promise} - Updated FAQ
   */
  async patchFaq(id, data) {
    try {
      const response = await apiClient.patch(`/admin/faqs/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error patching FAQ:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete FAQ
   * @param {number|string} id - FAQ ID
   * @returns {Promise} - Success status
   */
  async deleteFaq(id) {
    try {
      await apiClient.delete(`/admin/faqs/${id}/`);

      return {
        success: true,
        message: 'FAQ deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      return this.handleError(error);
    }
  }

  /**
   * Publish FAQ (change status from draft to published)
   * @param {number|string} id - FAQ ID
   * @returns {Promise} - Published FAQ
   */
  async publishFaq(id) {
    try {
      const response = await apiClient.post(`/admin/faqs/${id}/publish/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error publishing FAQ:', error);
      return this.handleError(error);
    }
  }

  /**
   * Toggle active status of FAQ
   * @param {number|string} id - FAQ ID
   * @returns {Promise} - Updated FAQ
   */
  async toggleActive(id) {
    try {
      const response = await apiClient.post(`/admin/faqs/${id}/toggle_active/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error toggling FAQ active status:', error);
      return this.handleError(error);
    }
  }

  /**
   * Export FAQs to Excel
   * @returns {Promise} - Excel file blob
   */
  async exportToExcel() {
    try {
      const response = await apiClient.get('/admin/faqs/export_excel/', {
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error exporting FAQs to Excel:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';

    if (error.response) {
      if (error.response.data) {
        // Extract error message from response
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else {
          // Try to get first error from field errors
          const firstKey = Object.keys(error.response.data)[0];
          if (firstKey && Array.isArray(error.response.data[firstKey])) {
            message = error.response.data[firstKey][0];
          }
        }
      }

      // Handle specific status codes
      if (error.response.status === 403) {
        message = 'Access denied. Admin privileges required.';
      } else if (error.response.status === 404) {
        message = 'FAQ not found.';
      }
    } else if (error.request) {
      message = 'Unable to connect to server. Please check your connection.';
    }

    return {
      success: false,
      error: message,
    };
  }
}

export default new FaqService();
